import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { MicOff } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDuration, fmtPhone, fmtShort, isoDay, relTime } from "@/lib/format";
import { EmptyState, Loading, Section } from "@/components/common";
import { cn } from "@/lib/utils";

const rateColor = (r) => (r == null ? "text-slate-500" : r >= 95 ? "text-emerald-300" : r >= 80 ? "text-amber-300" : "text-red-300");
const pctLabel = (r) => (r == null ? "—" : `${r}%`);

function AgentBar({ a }) {
  return (
    <li className="text-xs" data-testid={`recording-agent-${a.agent_user_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-200 truncate">{a.name}</span>
        <span className="font-mono text-slate-500 whitespace-nowrap">
          {a.recorded}/{a.recorded + a.missing} <span className={rateColor(a.rate)}>{pctLabel(a.rate)}</span>
          {a.missing ? <span className="text-red-300 ml-1.5">· {a.missing} missing</span> : null}
        </span>
      </div>
      <div className="h-1 bg-[#161f30] rounded mt-1 overflow-hidden"><div className="h-1 bg-emerald-500/70 transition-[width] duration-500" style={{ width: `${a.rate ?? 0}%` }} /></div>
    </li>
  );
}

function UnrecordedCall({ c }) {
  return (
    <li>
      <Link to={`/calls?call=${c.id}`} data-testid={`unrecorded-call-${c.id}`} className="flex items-center gap-2 py-1.5 px-1 text-xs row-hover rounded">
        <MicOff className="h-3 w-3 text-red-400 shrink-0" />
        <span className="font-mono text-slate-500 whitespace-nowrap">{fmtShort(c.created_at)}</span>
        <span className="text-slate-200 truncate">{c.agent_name}</span>
        <span className="font-mono text-slate-400 ml-auto whitespace-nowrap">{fmtPhone(c.client_number)} · {fmtDuration(c.duration)}</span>
        {c.unrecorded_alert?.status === "sent" && <span className="text-[10px] text-amber-300 font-mono">alerted</span>}
      </Link>
    </li>
  );
}

export function RecordingCompliance() {
  const [days, setDays] = useState(14);
  const q = useQuery({ queryKey: ["recording-compliance", days], queryFn: () => api.get("/recordings/compliance", { params: { date_from: isoDay(subDays(new Date(), days)), date_to: isoDay(new Date()) } }).then((r) => r.data) });
  const d = q.data;
  const kpis = d ? [["Calls", d.total, "text-slate-100"], ["Recorded", d.recorded, "text-emerald-300"], ["Missing", d.missing, d.missing ? "text-red-300" : "text-slate-400"], ["Rate", pctLabel(d.rate), rateColor(d.rate)]] : [];
  return (
    <Section
      title="Recording compliance"
      eyebrow={`Completed calls ≥ ${d ? Math.round(d.min_seconds / 60) : 2} min without an OpenPhone recording`}
      testId="recording-compliance-section"
      actions={
        <div className="inline-flex rounded-md border border-[#1f293d] bg-[#0f172a] p-0.5">
          {[7, 14, 30].map((n) => <button key={n} data-testid={`recording-range-${n}`} onClick={() => setDays(n)} className={cn("px-2 py-0.5 text-[11px] font-mono rounded transition-colors", days === n ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white")}>{n}d</button>)}
        </div>
      }
    >
      {q.isLoading || !d ? <Loading /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {kpis.map(([l, v, c]) => (
              <div key={l} className="bg-[#0f172a] border border-[#1f293d] rounded p-2" data-testid={`recording-kpi-${l.toLowerCase()}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{l}</div>
                <div className={cn("metric text-lg", c)}>{v}</div>
              </div>
            ))}
          </div>
          {d.agents.length ? <ul className="space-y-2" data-testid="recording-agent-list">{d.agents.map((a) => <AgentBar key={a.agent_user_id} a={a} />)}</ul> : <EmptyState icon={MicOff} title="No qualifying calls in range" />}
          {d.calls.length > 0 && (
            <div>
              <div className="eyebrow mb-1.5 text-red-300">Unrecorded calls · {d.calls.length}</div>
              <ul className="divide-y divide-[#1f293d] max-h-64 overflow-y-auto" data-testid="unrecorded-calls-list">{d.calls.map((c) => <UnrecordedCall key={c.id} c={c} />)}</ul>
            </div>
          )}
          <div className="text-[10px] text-slate-500" data-testid="recording-last-check">{d.pending ? `${d.pending} call(s) still awaiting check · ` : ""}Last check {d.last_check ? relTime(d.last_check) : "not yet run"}. Management is emailed per unrecorded call and in the daily rollup.</div>
        </div>
      )}
    </Section>
  );
}
